import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchApi, retryUnlessClientError } from "../../helpers/reactQueryApi";
import type {
  Answer,
  EditableQuestionnaire,
  QuestionnaireResults,
} from "./types";
import type { Activity, QuestionnaireObject } from "../../@types/activities";
import { toPayload, type EditorValues } from "./editor/editorOperations";
import type { AttachmentsPayload } from "./editor/useAttachmentsPayload";

export type QuestionnaireDetail = QuestionnaireObject & {
  activity_id: string;
  activity_url: string;
};

type SavedQuestionnaire = { id: string; activity_id: string };

export function useQuestionnaire(id: string | undefined) {
  return useQuery<QuestionnaireDetail>(`/questionnaires/${id}.json`, {
    enabled: !!id,
    retry: retryUnlessClientError,
  });
}

export function useEditableQuestionnaire(id: string | undefined) {
  return useQuery<EditableQuestionnaire>(`/questionnaires/${id}/edit.json`, {
    enabled: !!id,
    retry: retryUnlessClientError,
    // the editor must start from the stored state, not from a cached older one
    cacheTime: 0,
  });
}

export function useQuestionnaireResults(id: string | undefined) {
  return useQuery<QuestionnaireResults>(`/questionnaires/${id}/results.json`, {
    enabled: !!id,
    retry: retryUnlessClientError,
  });
}

export function useCreateQuestionnaire() {
  return useMutation<
    SavedQuestionnaire,
    Error,
    {
      values: EditorValues;
      activitystreamSlugs: string[];
      attachments: AttachmentsPayload;
    }
  >(({ values, activitystreamSlugs, attachments }) =>
    fetchApi("/questionnaires.json", {
      method: "POST",
      body: {
        questionnaire: {
          ...toPayload(values),
          ...attachments,
          activitystream_slugs: activitystreamSlugs,
        },
      },
    }),
  );
}

export function useUpdateQuestionnaire(id: string) {
  const queryClient = useQueryClient();

  return useMutation<
    EditableQuestionnaire,
    Error,
    { values: EditorValues; attachments: AttachmentsPayload }
  >(
    ({ values, attachments }) =>
      fetchApi(`/questionnaires/${id}.json`, {
        method: "PUT",
        body: { questionnaire: { ...toPayload(values), ...attachments } },
      }),
    {
      onSuccess: (data) => {
        // detail, edit and results keys all start with the questionnaire path
        queryClient.invalidateQueries({
          predicate: (query) =>
            String(query.queryKey[0]).startsWith(`/questionnaires/${id}`),
        });
        queryClient.invalidateQueries(`/activities/${data.activity_id}`);
        queryClient.invalidateQueries(["activities"]);
      },
    },
  );
}

export function useSubmitQuestionnaire(id: string) {
  return useMutation<
    Activity,
    Error,
    { answers: Answer[]; anonymous: boolean }
  >(({ answers, anonymous }) =>
    fetchApi(`/questionnaires/${id}/submission.json`, {
      method: "PUT",
      body: { submission: { answers, anonymous } },
    }),
  );
}
